import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconTrash } from '@medflex/components/stories/Icons';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import Button from '@medflex/components/stories/Button';
import SendButton from '@medflex/components/stories/SendButton';
import Tooltip from '@medflex/components/stories/Tooltip';
import MessageComposer from '@medflex/components/stories/MessageComposer';
import useKeys from '@medflex/hooks/useKeys';
import { showAlertToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import useUppy from '@medflex/hooks/useUppy';
import { FileItem, FileInput } from '@medflex/components/stories/FileUpload';
import useUserSession from '@medflex/hooks/useUserSession';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import isStaffUserId from '@medflex/utils/isStaffUserId';
import Logger from '@medflex/utils/Logger';
import {
  Player,
  Recorder,
  Trigger,
  useAudioPermission,
} from '@medflex/components/stories/VoiceMessage';
import isAnySessionChatId from '@medflex/utils/isAnySessionChatId';
import { AUDIO_MESSAGE_PREFIX, AUDIO_MESSAGE_MAX_DURATION } from '@medflex/utils/constants';
import Badge, { KINDS as BADGE_KINDS } from '@medflex/components/stories/Badge';
import InfoBanner, { KINDS as INFO_BANNER_KINDS } from '@medflex/components/stories/InfoBanner';
import config from '@medflex/utils/config';
import { FEATURE_PROP_TYPE } from '@medflex/utils/subscriptionEnums';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { useHistory, useLocation } from 'react-router';
import useIsModal, { MODALS } from '../../hooks/useIsModal';
import useClipboard from '../../hooks/useClipboard';
import useUnloadWarning from '../../hooks/useUnloadWarning';
import useSelectedContact from '../../hooks/useSelectedContact';
import useSelectedMainContact from '../../hooks/useSelectedMainContact';
import useModal from '../../hooks/useModal';
import ComposerContextMenu from './ComposerContextMenu';
import { ActionItemsPropType } from './propTypes';
import useSnippets from '../../hooks/useSnippets';
import useLocationSearchParams from '../../../hooks/useLocationSearchParams';

const getUploadMetaData = (superuser, userId, mainContactUserId, debugData) => {
  const metaData = {
    sender: superuser.id,
    recipient: userId,
  };

  if (isPatientUserId(superuser.id) && mainContactUserId !== userId) {
    metaData.sender = userId;
    metaData.recipient = mainContactUserId;
  }

  // logging needed for MFLEX-2662, checking if recepient is staff member
  if (metaData.recipient && isStaffUserId(metaData.recipient)) {
    Logger.error(
      new Error(
        `Recipient is staff member ${JSON.stringify({
          metaData,
          ...debugData,
        })}`,
      ),
    );
  }

  return metaData;
};

const ComposerAreaModule = ({
  actionItems,
  dropTarget,
  mainContactUserId,
  id,
  userId,
  sendMessage,
  errors,
  isSendingMessage,
  chatCharacters,
  preventAudioRecording = false,
  calcComposerMaxRows,
  isPamSession,
  selectedSnippet,
  shouldShowChatDisabledInfo = false,
}) => {
  const dispatch = useDispatch();
  const [isRecording, setIsRecording] = useState(false);
  // shouldFocusInput is used to handle focus on text area, which shows the keyboard on mobile
  // for more details and all the cases see https://medflexgmbh.atlassian.net/browse/MMA-223
  const [shouldFocusInput, setShouldFocusInput] = useState(false);
  const { getUserAudio, granted: audioPermissionGranted } = useAudioPermission();
  const [recording, setRecording] = useState(null);
  const { isMeta } = useKeys();
  const superuser = useSelector(({ superuser: user }) => user);
  const [value, setValue] = useState('');
  const isWideScreen = useIsWideScreen();
  const { isModal } = useIsModal();
  const messageComposerTextAreaRef = useRef();
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation('messenger');
  const { getClipboard, updateClipboard } = useClipboard();
  const { session } = useUserSession();
  const { open } = useModal();
  const [snippetFiles, setSnippetFiles] = useState([]);
  const [currentClientHeight, setCurrentClientHeight] = useState();
  const { snippets } = useSnippets();
  const { snippetId } = useLocationSearchParams();
  const { replace } = useHistory();
  const { search } = useLocation();

  const snippet = useMemo(() => {
    if (snippets?.length > 0 && snippetId) {
      return snippets.find(({ id }) => snippetId === id);
    }
    return null;
  }, [snippets, snippetId]);

  // here used just for logging (MFLEX-2662)
  const { selectedContact } = useSelectedContact();
  const selectedMainContact = useSelectedMainContact();
  const debugData = { selectedContact, selectedMainContact };
  // = = = = = = = = = = = = = = = = = = = =

  const { userToFileUploadMappings } = useSelector(({ fileUploads }) => fileUploads);
  const userUploadIds = useMemo(() => userToFileUploadMappings[id], [userToFileUploadMappings, id]);

  const {
    lastAddedFilesIds,
    files,
    register: registerFileInput,
    uploading,
    upload,
    reset,
    removeFile,
    errors: fileErrors,
    retry,
    addBlob,
  } = useUppy({
    dropTarget,
    endpoint: isAnySessionChatId(id)
      ? `${config.PUBLIC_INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions/${id}/uploads`
      : `${config.INSTANT_MESSAGE_SERVICE_ENDPOINT}/uploads`,
    multiple: true,
    onRestrictionFailed: ({ name: fileName }, { message }) => {
      trackEvent({
        zone: 'ComposerArea',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.INPUT_FILE,
        actionText: 'upload failed',
      });
      showAlertToast(t('ComposerAreaModule.errors.file.title', { fileName }), message, {
        toastId: 'fileUploadFailed',
      });
    },
    headers: {
      AUTHORIZATION: `Bearer ${session.access_token || ''}`,
      ...(userId && {
        'Upload-Metadata2': btoa(
          JSON.stringify(getUploadMetaData(superuser, userId, mainContactUserId, debugData)),
        ),
      }),
    },
  });

  useUnloadWarning(uploading);

  const deleteRecording = () => {
    window.URL.revokeObjectURL(recording);
    setRecording(null);
  };

  const limitReached = useMemo(
    () =>
      chatCharacters &&
      chatCharacters.limit &&
      chatCharacters.limit - chatCharacters.count - value.length <= 0,
    [value, chatCharacters],
  );

  useEffect(() => {
    if (calcComposerMaxRows) {
      const resizeEventListener = () => setCurrentClientHeight(dropTarget.current.clientHeight);
      window.addEventListener('resize', resizeEventListener);
      setTimeout(resizeEventListener, 100);
      return () => {
        window.removeEventListener('resize', resizeEventListener);
      };
    }

    return undefined;
  }, [calcComposerMaxRows, dropTarget]);

  useEffect(() => {
    if (!errors && reset && !isSendingMessage && submitted) {
      reset();
      setValue('');
      deleteRecording();
      dispatch.fileUploads.addFileReferences({
        referenceId: id,
        filesIds: [],
      });
      setSubmitted(false);
    }
  }, [errors, reset, isSendingMessage, submitted]);

  useEffect(() => {
    if (limitReached) {
      open({ modal: MODALS.featureUpgradeChat });
    }
  }, [value]);

  const maxRows = useMemo(() => {
    if (currentClientHeight) {
      return Math.round((currentClientHeight * 0.35) / 24);
    }
    return 8;
  }, [currentClientHeight]);

  const fileUploadCompleted = useMemo(() => {
    if (!uploading && files.length > 0) {
      if (!files.some(({ progress }) => !progress.uploadComplete)) {
        return true;
      }
    }
    return false;
  }, [files, uploading]);

  const retryFailedFiles = useCallback(
    () => Promise.all(fileErrors.map(error => retry(error.file))),
    [fileErrors],
  );

  const handleStart = () => {
    setIsRecording(true);
  };

  const handleStop = () => {
    setIsRecording(false);
    setShouldFocusInput(false);
    if (messageComposerTextAreaRef.current) {
      messageComposerTextAreaRef.current.blur();
    }
  };

  const handleRecordingFinished = data => {
    const { duration } = data;
    if (duration >= 2) {
      setRecording(data);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const send = async () => {
    const getFileIdFromUri = uri => uri.split('/').reverse()[0];
    const text = value.trim();

    if (recording && !fileUploadCompleted) {
      const blob = await fetch(recording.url).then(r => r.blob());
      addBlob(blob, {
        name: `${AUDIO_MESSAGE_PREFIX}-${new Date().toISOString()}`,
        type: blob.type,
        meta: { duration: recording.duration },
      });
      upload();
      return;
    }

    if (files.length > 0 && !fileUploadCompleted) {
      if (fileErrors.length > 0) {
        retryFailedFiles();
        return;
      }
      upload();
      return;
    }

    const getActionText = success => {
      const status = success ? 'Success' : 'Failed';
      if (files.length) {
        if (recording) {
          return `Send Audio Message - ${status}`;
        }
        return `Send File - ${status}`;
      }
      return `Send Message ${
        userId && isSupportPatient({ id: userId }) ? 'to support' : ''
      } - ${status}`;
    };

    try {
      await sendMessage({
        ...(userId && { recipient: userId }),
        ...(text && { text }),
        ...(files.length && {
          files: files.map(file => getFileIdFromUri(file.uploadURL)),
        }),
        ...(snippetFiles.length && { snippetFiles: snippetFiles.map(({ id }) => id) }),
      });
      setSnippetFiles([]);
      setSubmitted(true);
      trackEvent({
        zone: 'Chat',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.INPUT,
        actionText: getActionText(true),
      });
    } catch (e) {
      trackEvent({
        zone: 'Chat',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.INPUT,
        actionText: getActionText(),
      });

      if (e?.response?.status === 403) {
        showAlertToast(
          t('ComposerAreaModule.errors.sendMessageForbidden.title'),
          t('ComposerAreaModule.errors.sendMessageForbidden.description'),
          { toastId: 'sendMessageForbidden' },
        );
      }
    }
  };

  useEffect(() => {
    if (id) {
      const store = JSON.parse(localStorage.getItem('chatRoomModule_clipboard')) || {};
      setValue(store[`${id}${superuser.id}`] || store[id] || '');
    }
  }, [id]);

  useEffect(() => {
    if (fileUploadCompleted) send();
  }, [fileUploadCompleted]);

  useEffect(() => {
    if (id) {
      const {
        [`${id}${superuser.id}`]: storedValue = '', // default has to be the same as the default of value
        ...rest
      } = getClipboard();

      if (storedValue !== value) {
        updateClipboard({
          ...rest,
          ...(value && { [`${id}${superuser.id}`]: value }),
        });
      }
    }
  }, [id, value]);

  useEffect(() => {
    if (messageComposerTextAreaRef.current && !isModal && isWideScreen && !isPamSession) {
      messageComposerTextAreaRef.current.focus();
      setSelectionStart(null);
      setSelectionEnd(null);
    }
  }, [isModal, isWideScreen, messageComposerTextAreaRef, id, isPamSession]);

  useEffect(() => {
    /* set cursor to the end initially */
    if (messageComposerTextAreaRef.current && isWideScreen) {
      messageComposerTextAreaRef.current.setSelectionRange(value.length, value.length);
    }
  }, [messageComposerTextAreaRef, isWideScreen]);

  const handleKeys = useCallback(
    event => {
      if (
        (event.ctrlKey || isMeta) &&
        event.key === 'Enter' &&
        (value.text !== '' || files.length)
      ) {
        send();
      }
    },
    [files.length, isMeta, send, value.text],
  );

  const handleFocus = useCallback(() => {
    setShouldFocusInput(true);
  }, []);

  useEffect(() => {
    const composerTextRef = messageComposerTextAreaRef.current;
    if (messageComposerTextAreaRef.current) {
      composerTextRef.addEventListener('keydown', handleKeys);
      composerTextRef.addEventListener('focus', handleFocus);

      return () => {
        if (composerTextRef) {
          composerTextRef.removeEventListener('keydown', handleKeys);
          composerTextRef.removeEventListener('focus', handleFocus);
        }
      };
    }
    return undefined;
  }, [isMeta, value, handleKeys, handleFocus]);

  //  handle file upload to user mappings
  useEffect(() => {
    if (id) {
      dispatch.fileUploads.addFileReferences({
        referenceId: id,
        filesIds: lastAddedFilesIds,
      });
    }
  }, [lastAddedFilesIds]);

  useEffect(
    () => () =>
      dispatch.fileUploads.addFileReferences({
        referenceId: id,
        filesIds: [],
      }),
    [],
  );

  const handleSubmit = e => {
    send();
    e.preventDefault();
    if (messageComposerTextAreaRef.current && shouldFocusInput) {
      messageComposerTextAreaRef.current.focus();
    }
  };

  const saveSelection = () => {
    if (messageComposerTextAreaRef.current) {
      setSelectionStart(messageComposerTextAreaRef.current.selectionStart);
      setSelectionEnd(messageComposerTextAreaRef.current.selectionEnd);
    }
  };

  const insertSnippet = useCallback(
    ({ text, files = [] }) => {
      if (text) {
        setValue(
          `${value.slice(0, selectionStart)}${text}${value.slice(selectionEnd, value.length)}`,
        );

        if (messageComposerTextAreaRef.current) {
          setTimeout(() => {
            messageComposerTextAreaRef.current.setSelectionRange(
              selectionStart + text.length,
              selectionStart + text.length,
            );
            messageComposerTextAreaRef.current.focus();
          });
        }
      }

      if (files.length) {
        setSnippetFiles(files);
      }
    },
    [selectionEnd, selectionStart, value],
  );

  const handleMessageChange = ({ target }) => {
    if (!(limitReached && value.length < target.value.length)) {
      const inputValue =
        chatCharacters && chatCharacters.limit
          ? target.value.substr(0, chatCharacters.limit - chatCharacters.count)
          : target.value;
      setValue(inputValue);
    }
  };

  useEffect(() => {
    if (snippet) {
      insertSnippet(snippet);
      const urlSearchParams = new URLSearchParams(search);
      urlSearchParams.delete('snippetId');
      replace(`?${urlSearchParams.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snippet, replace, search]);

  useEffect(() => {
    if (!selectedSnippet) return;
    insertSnippet(selectedSnippet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSnippet]);

  useEffect(() => {
    const composerTextRef = messageComposerTextAreaRef.current;
    const handlePaste = e => {
      const { files } = e.clipboardData;
      if (!files.length) return;
      e.preventDefault();
      [...files].forEach(file => addBlob(file, { name: file.name, type: file.type }));
    };
    composerTextRef.addEventListener('paste', handlePaste);

    return () => composerTextRef.removeEventListener('paste', handlePaste);
  }, [addBlob]);

  return (
    <>
      <div data-component="ComposerArea">
        <form onSubmit={handleSubmit} className="flex flex-col w-full">
          <div className="border-t bg-white border-slate-300 py-2 px-2 lg:py-4">
            <div className="flex">
              <div className="flex-shrink-0 self-end">
                {recording ? (
                  <Tooltip title={t('VoiceMessage.Player.delete')}>
                    <Button kind="secondary:ghost" shape="circle" onClick={deleteRecording}>
                      <IconTrash size="6" />
                    </Button>
                  </Tooltip>
                ) : (
                  <ComposerContextMenu
                    registerFileInput={registerFileInput}
                    actionItems={actionItems}
                    t={t}
                    isWideScreen={isWideScreen}
                    isDisabled={isSendingMessage || uploading || recording || isRecording}
                    setValue={setValue}
                    insertSnippet={insertSnippet}
                    id={superuser.id}
                    isPamSession={isPamSession}
                  />
                )}
              </div>
              <div className="flex flex-auto overflow-hidden">
                <MessageComposer
                  inputValue={value}
                  disabled={uploading}
                  className={`relative ml-2 flex-grow justify-end lg:ml-4 ${
                    isRecording ? 'bg-blue-500 text-white' : ''
                  }`}
                  inputMaxRows={maxRows}
                  state={isSendingMessage ? 'loading' : 'composing'}
                  onChange={handleMessageChange}
                  onBlur={saveSelection}
                  ref={messageComposerTextAreaRef}
                  files={
                    userUploadIds && (
                      <>
                        {files.map(
                          file =>
                            userUploadIds.includes(file.id) && (
                              <FileItem
                                key={`ChatModule__FileItem${file.id}`}
                                type={file.type}
                                name={file.name}
                                size={file.size}
                                percentage={file.progress && file.progress.percentage}
                                hasError={!!file.error}
                                onDelete={() => removeFile({ id: file.id })}
                                small
                              />
                            ),
                        )}
                        {snippetFiles.length > 0 &&
                          snippetFiles.map(file => (
                            <FileItem
                              key={`ChatModule__FileItem${file.id}`}
                              type={file.mediaType}
                              name={file.name}
                              size={file.size}
                              percentage={file.percentage}
                              extension={file.extension}
                              hasError={!!file.error}
                              onDelete={() =>
                                setSnippetFiles(snippetFiles.filter(({ id }) => id !== file.id))
                              }
                              small
                            />
                          ))}
                      </>
                    )
                  }
                  textHidden={!!(isRecording || recording)}
                  inputPlaceholder={t('ComposerAreaModule.input_placeholder')}
                >
                  {recording && (
                    <Player
                      caption={t('ComposerAreaModule.recording')}
                      src={recording.url}
                      initialDuration={recording.duration}
                    />
                  )}
                  {!recording && files.length === 0 && (
                    <Recorder
                      className="flex-grow"
                      recording={isRecording}
                      onFinished={handleRecordingFinished}
                      hidden={!isRecording}
                      onStop={handleStop}
                      maxDuration={AUDIO_MESSAGE_MAX_DURATION}
                    />
                  )}
                </MessageComposer>
              </div>
              {!value.length &&
              !recording &&
              files.length === 0 &&
              snippetFiles.length === 0 &&
              !preventAudioRecording ? (
                !recording && (
                  <Trigger
                    className={`h-12 w-12 ml-2 ${
                      isRecording ? 'animate-pulse text-blue-500' : 'text-blue-500'
                    }`}
                    onStart={handleStart}
                    onStop={handleStop}
                    audioPermissionGranted={audioPermissionGranted}
                    onHandleAudioPermission={getUserAudio}
                    disabled={uploading}
                    tooltip={
                      // eslint-disable-next-line no-nested-ternary
                      audioPermissionGranted
                        ? isRecording
                          ? t('ComposerAreaModule.recordingTrigger.stop')
                          : t('ComposerAreaModule.recordingTrigger.start')
                        : null
                    }
                  />
                )
              ) : (
                <SendButton
                  className="ml-4 flex-shrink-0 self-end"
                  small={!isWideScreen}
                  loading={uploading || isSendingMessage}
                  enabled={value !== '' || files.length > 0 || recording !== null}
                />
              )}
            </div>
            {isPatientUserId(superuser.id) && (
              <FileInput {...registerFileInput()}>
                {({ select }) => (
                  <>
                    <div className="mb-2" />
                    <button
                      type="button"
                      className="flex flex-row text-slate-500 hover:text-blue-600 ml-2 lg:ml-4"
                      onClick={select}
                    >
                      <PaperClipIcon className="w-6" />
                      <span className="ml-3">{t('global:actions.file_upload.menu')}</span>
                    </button>
                  </>
                )}
              </FileInput>
            )}
            {shouldShowChatDisabledInfo && (
              <InfoBanner kind={INFO_BANNER_KINDS.INFO} className="mx-auto mt-4 px-4 py-1">
                <LightBulbIcon className="w-4 inline-block" />
                <span className="ml-2 text-sm">{t('ComposerAreaModule.chatDisabledInfo')}</span>
              </InfoBanner>
            )}
          </div>
        </form>
      </div>
      {chatCharacters && chatCharacters.limit && (
        <InfoBanner
          kind={INFO_BANNER_KINDS.SIGNAL}
          className="cursor-pointer py-2 px-5 text-center text-slate-500 w-full"
          onClick={() => open({ modal: MODALS.featureUpgradeChat })}
        >
          {limitReached
            ? t('ComposerAreaModule.remainingCharacters_zero', {
                allowHtml: true,
              })
            : t('ComposerAreaModule.remainingCharacters', {
                count: chatCharacters.limit - chatCharacters.count - value.length,
              })}
          {isWideScreen && (
            <Badge className="ml-2" kind={BADGE_KINDS.SIGNAL}>
              Upgrade
            </Badge>
          )}
        </InfoBanner>
      )}
    </>
  );
};

ComposerAreaModule.propTypes = {
  dropTarget: PropTypes.any,
  mainContactUserId: PropTypes.string,
  id: PropTypes.string,
  userId: PropTypes.string,
  sendMessage: PropTypes.func,
  errors: PropTypes.any,
  isSendingMessage: PropTypes.bool,
  actionItems: ActionItemsPropType,
  chatCharacters: FEATURE_PROP_TYPE,
  calcComposerMaxRows: PropTypes.bool,
  preventAudioRecording: PropTypes.bool,
  isPamSession: PropTypes.bool,
  selectedSnippet: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string,
    files: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        mediaType: PropTypes.string.isRequired,
        bytes: PropTypes.number.isRequired,
        detectedMediaType: PropTypes.string.isRequired,
      }),
    ),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ),
  }),
  shouldShowChatDisabledInfo: PropTypes.bool,
};

ComposerAreaModule.defaultProps = {
  actionItems: [],
  calcComposerMaxRows: false,
};

export default ComposerAreaModule;
